import { observer } from 'mobx-react'
import React from 'react'
import JobViewModel from '../JobViewModel'
import { match } from 'utils/pattern-match-util'
import ConsultationPendingBookingModal from '../ConsultationPendingBookingModal'
import {
  CardHeader,
  CardTitle,
  CardContent,
  CardContentDescription,
  Card,
} from '../../../../../components/Card'
import ProgressBar from '../../../../../components/ProgressBar'
import Button from '../../../../../components/Button'
import {
  ConsultationCallProgressSteps,
  ConsultationCallProgressStepsIds,
} from '../../../../consultations/Consultation'
import RoomCard from '../RoomCard'
import { formatToLongDateWithTimeZone } from '../../../../../utils/date-util'
import styles from './ConsultationCallCard.sass'

interface Props {
  vm: JobViewModel
}

export default observer(function ConsultationCallCard({ vm }: Props) {
  const consultation = vm.job.consultation
  if (!consultation) {
    return <></>
  }

  if (vm.consultationIsPendingScheduling) {
    return <PendingSchedulingCard vm={vm} />
  }

  const callStep = ConsultationCallProgressSteps[consultation.callStep]

  return (
    <RoomCard
      vm={vm.roomVM}
      titleContent={
        <RoomCardTitle
          skipScheduling={consultation.skipScheduling}
          callStep={consultation.callStep}
        />
      }
      descriptionContent={
        <RoomCardDescription
          startTime={consultation.bookedTime?.startTime}
          skipScheduling={consultation.skipScheduling}
          callStep={consultation.callStep}
        />
      }
      progressBar={
        !consultation.skipScheduling && (
          <ProgressBar
            bars={[
              {
                progress: callStep.completed,
              },
              {
                progress: callStep.pending,
                style: 'striped',
              },
            ]}
          />
        )
      }
      buttonGroup={
        <>
          {consultation.canClose && (
            <Button
              type={'secondary'}
              size="wide-slim"
              disabled={!consultation.canClose}
              onClick={() => vm.requestCloseAsCollaborator()}
              dataTest="consultation-complete-job-button"
            >
              Complete Job
            </Button>
          )}
          <DownloadCalendarButton vm={vm} />
        </>
      }
    />
  )
})

export const PendingSchedulingContent = observer(
  function PendingSchedulingCard({ vm }: Props) {
    return (vm.isClaimedJob || vm.isOnHold) &&
      vm.consultationIsPendingBooking ? (
      <>
        <ConsultationPendingBookingModal
          vm={vm.consultationPendingBookingModalVM}
        />
        <CardHeader>
          <CardTitle dataTest="current-task-title">
            {vm.job.consultation?.previouslyBooked
              ? 'Rescheduling requested'
              : 'Call scheduled'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ProgressBar bars={[{ progress: 100, style: 'striped' }]} />
          <br />
          <CardContentDescription>
            <p>
              Your client has{' '}
              {vm.job.consultation?.previouslyBooked
                ? 'requested to reschedule'
                : 'scheduled a call'}
              . Please review and select a new date and time to continue.
            </p>
          </CardContentDescription>
        </CardContent>
        <CardContent>
          <Button
            type="primary"
            size={'wide-slim'}
            onClick={vm.promptForTimeSlotSelection}
            disabled={vm.promptForTimeSlotSelection.pending}
            loading={vm.promptForTimeSlotSelection.pending}
            dataTest={'book-consultation-button'}
          >
            Select {vm.job.consultation?.previouslyBooked ? 'new' : ''}{' '}
            appointment
          </Button>
        </CardContent>
      </>
    ) : (
      <>
        <CardHeader>
          <CardTitle dataTest="current-task-title">
            Prepare for the consultation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CardContentDescription>
            <p>{vm.consultationPendingSchedulingText}</p>
          </CardContentDescription>
        </CardContent>
      </>
    )
  },
)

export const DownloadCalendarButton = observer(function DownloadCalendarButton({
  vm,
}: Props) {
  return (
    vm.isConsultationBookedAndActive && (
      <Button
        type={'secondary'}
        size={'wide-slim'}
        disabled={vm.downloadConsultationCalendar.pending}
        loading={vm.downloadConsultationCalendar.pending}
        onClick={vm.downloadConsultationCalendar}
        dataTest="consultation-download-calendar-button"
      >
        Add To Calendar
      </Button>
    )
  )
})

const RoomCardTitle = observer(function RoomCardTitle({
  skipScheduling,
  callStep,
}: {
  skipScheduling: boolean
  callStep: ConsultationCallProgressStepsIds
}) {
  if (skipScheduling) {
    return <>Prepare for the consultation</>
  }

  return match(callStep, {
    PENDING: () => <>Prepare for the consultation</>,
    IN_PROGRESS: () => <>Consultation in progress</>,
    DONE: () => <>Finalize the consultation</>,
    COMPLETED: () => <>Job Completed</>,
    UNKNOWN: () => undefined,
  })
})

const RoomCardDescription = observer(function RoomCardDescription({
  skipScheduling,
  callStep,
  startTime,
}: {
  startTime: Date | undefined
  skipScheduling: boolean
  callStep: ConsultationCallProgressStepsIds
}) {
  if (skipScheduling) {
    return (
      <>
        Use the chat to arrange a date and time for the call with your client.
        Once a time has been agreed on, please join the call.
      </>
    )
  }

  return match(callStep, {
    PENDING: () => (
      <>
        <p>
          Call scheduled with client on{' '}
          <span className={styles.important}>
            {` ${formatToLongDateWithTimeZone(startTime)}. `}
          </span>
        </p>
      </>
    ),
    IN_PROGRESS: () => <>The consultation is currently in progress.</>,
    DONE: () => (
      <>Start another call with the client, or proceed to complete the job.</>
    ),
    COMPLETED: () => (
      <>The consultation has been completed and the room has been closed.</>
    ),
    UNKNOWN: () => undefined,
  })
})

const PendingSchedulingCard = observer(function PendingSchedulingCard({
  vm,
}: Props) {
  return (
    <Card>
      <PendingSchedulingContent vm={vm} />
    </Card>
  )
})
